import { useRef, useState, useCallback, useMemo, useEffect, memo } from "react";
import { Box, IconButton, Button, Typography, Chip, Collapse, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Add, Delete, ExpandMore, ExpandLess } from "@mui/icons-material";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast } from "react-toastify";
import api from "../../../api";
import { inferBlankLabelPosition, getFixedMarksPerBlankLabel } from "../../../utils/fillQuestionUtils";

const BLANK_ANSWER_FORMATS = ["bold", "italic", "image"];
const BLANK_ANSWER_MATH_FORMATS = ["bold", "italic", "image", "script"];

const CHAR_GROUPS = [
  {
    group: "Powers",
    chars: [
      { label: "²", title: "squared" },
      { label: "³", title: "cubed" },
      { label: "⁴", title: "to the 4th" },
      { label: "⁵", title: "to the 5th" },
      { label: "⁶", title: "to the 6th" },
      { label: "⁷", title: "to the 7th" },
      { label: "⁸", title: "to the 8th" },
      { label: "⁹", title: "to the 9th" },
      { label: "¹⁰", title: "to the 10th" },
      { label: "ⁿ", title: "to the nth" },
      { label: "⁰", title: "superscript 0" },
      { label: "¹", title: "superscript 1" },
    ],
  },
  {
    group: "Fractions",
    chars: [
      { label: "½", title: "one half" },
      { label: "¼", title: "one quarter" },
      { label: "¾", title: "three quarters" },
      { label: "⅓", title: "one third" },
      { label: "⅔", title: "two thirds" },
      { label: "⅕", title: "one fifth" },
      { label: "⅖", title: "two fifths" },
      { label: "⅗", title: "three fifths" },
      { label: "⅘", title: "four fifths" },
      { label: "⅛", title: "one eighth" },
      { label: "⅜", title: "three eighths" },
      { label: "⅝", title: "five eighths" },
      { label: "⅞", title: "seven eighths" },
    ],
  },
  {
    group: "Operators",
    chars: [
      { label: "×", title: "multiply" },
      { label: "÷", title: "divide" },
      { label: "±", title: "plus or minus" },
      { label: "√", title: "square root" },
      { label: "∛", title: "cube root" },
      { label: "≠", title: "not equal to" },
      { label: "≤", title: "less than or equal to" },
      { label: "≥", title: "greater than or equal to" },
      { label: "∞", title: "infinity" },
    ],
  },
  {
    group: "Shapes",
    chars: [
      { label: "□", title: "square / rectangle" },
      { label: "○", title: "circle" },
      { label: "☆", title: "star" },
      { label: "△", title: "triangle" },
    ],
  },
  {
    group: "Geometry & Measure",
    chars: [
      { label: "°", title: "degrees" },
      { label: "∠", title: "angle" },
      { label: "⊥", title: "perpendicular" },
      { label: "∥", title: "parallel" },
      { label: "π", title: "pi" },
      { label: "cm²", title: "square centimetres" },
      { label: "m²", title: "square metres" },
      { label: "km²", title: "square kilometres" },
      { label: "cm³", title: "cubic centimetres" },
      { label: "m³", title: "cubic metres" },
    ],
  },
  {
    group: "Currency & Other",
    chars: [
      { label: "£", title: "pound sterling" },
      { label: "p", title: "pence" },
      { label: "%", title: "percent" },
      { label: "∑", title: "sum" },
      { label: "→", title: "arrow / maps to" },
    ],
  },
];

/**
 * Single special-character ("Insert character") palette. Rendered ONCE per
 * editor section and shared by every blank/box, instead of one panel per box —
 * the previous per-box rendering created dozens of panels (e.g. a 9-cell table)
 * and made typing laggy. `onInsert(char)` is wired to whichever blank is
 * currently focused (see `charTargetRef` in BlankAnswerEditor).
 */
export const SpecialCharPanel = memo(function SpecialCharPanel({ onInsert }) {
  const [open, setOpen] = useState(true);
  return (
    <Box sx={{ mb: 1, bgcolor: "#f5f3ff", borderRadius: 1, border: "1px solid #ede9fe", overflow: "hidden" }}>
      <Box
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 0.75, py: 0.5, cursor: "pointer",
          "&:hover": { bgcolor: "rgba(124,58,237,0.06)" },
        }}
      >
        <Typography variant="caption" sx={{ color: "#7c3aed", fontWeight: 600 }}>
          Insert character
        </Typography>
        <IconButton size="small" onMouseDown={(e) => e.preventDefault()} sx={{ p: 0.25, color: "#7c3aed" }}>
          {open ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 0.75, pb: 0.75 }}>
          {CHAR_GROUPS.map(({ group, chars }) => (
            <Box key={group} sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: "#9ca3af", fontSize: "0.65rem", display: "block", mb: 0.25 }}>
                {group}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                {chars.map(({ label, title }) => (
                  <Button
                    key={label}
                    size="small"
                    title={title}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onInsert(label);
                    }}
                    sx={{
                      minWidth: 36, height: 26, px: 0.5, fontSize: "0.82rem", fontFamily: "monospace",
                      border: "1px solid #ddd6fe", bgcolor: "#fff", color: "#3730a3", lineHeight: 1,
                      textTransform: "none", "&:hover": { bgcolor: "#ede9fe" },
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
});

/**
 * Rich-text answer editor for a single blank.
 *
 * Lazy-mounts the Quill editor only when clicked so that N blanks on the page
 * do not freeze the browser (only one Quill instance is alive at a time).
 * At rest it shows a styled preview; on click it activates the editor with a
 * mini toolbar (bold, italic, image, clean).
 *
 * When `charTargetRef` (a shared ref) is provided, the editor does NOT render
 * its own character panel — instead, on focus it registers its `insertChar`
 * into `charTargetRef.current` so a single shared {@link SpecialCharPanel}
 * inserts into whichever blank is active. Without `charTargetRef` it falls back
 * to rendering its own inline panel (back-compat for standalone usages).
 */
export const BlankAnswerEditor = ({ value, onChange, placeholder, charTargetRef, showMathTools = false, quillTargetRef }) => {
  const [editing, setEditing] = useState(false);
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const uploadingRef = useRef(false);

  // Image toolbar button handler — uploads file to R2 and inserts the URL
  const imageHandler = useCallback(function () {
    // `this` is the Quill toolbar context; Quill binds it when calling handlers
    const quill = this.quill;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png,image/jpeg,image/webp";
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append("image", file);
        const res = await api.post("/admin/question-bank/upload-image", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = res.data?.data?.image_url;
        if (url) {
          const range = quill.getSelection(true) || { index: quill.getLength() };
          quill.insertEmbed(range.index, "image", url, "user");
          quill.setSelection(range.index + 1);
        }
      } catch {
        toast.error("Image upload failed");
      }
    };
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: showMathTools
          ? [["bold", "italic"], [{ script: "sub" }, { script: "super" }], ["image"], ["clean"]]
          : [["bold", "italic"], ["image"], ["clean"]],
        handlers: { image: imageHandler },
      },
    }),
    [imageHandler, showMathTools]
  );

  // Detects pasted/dropped base64 images and uploads them to R2
  const handleChange = async (html) => {
    const base64Re = /src="(data:image\/[^"]+)"/g;
    if (!base64Re.test(html) || uploadingRef.current) {
      onChange(html);
      return;
    }
    uploadingRef.current = true;
    let processed = html;
    for (const match of [...html.matchAll(/src="(data:image\/[^"]+)"/g)]) {
      try {
        const fetchRes = await fetch(match[1]);
        const blob = await fetchRes.blob();
        const ext = blob.type.split("/")[1] || "png";
        const file = new File([blob], `pasted.${ext}`, { type: blob.type });
        const formData = new FormData();
        formData.append("image", file);
        const res = await api.post("/admin/question-bank/upload-image", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = res.data?.data?.image_url;
        if (url) processed = processed.replace(match[0], `src="${url}"`);
      } catch {
        toast.error("Pasted image upload failed");
      }
    }
    uploadingRef.current = false;
    onChange(processed);
  };

  // Uses Quill's own API to insert at the current cursor position
  const insertChar = useCallback((char) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const range = quill.getSelection() ?? { index: Math.max(0, quill.getLength() - 1) };
    quill.insertText(range.index, char, "user");
    quill.setSelection(range.index + char.length);
  }, []);

  // Register this editor as the active insert target for the shared char panel
  const registerActive = useCallback(() => {
    if (charTargetRef) charTargetRef.current = insertChar;
    if (quillTargetRef) {
      quillTargetRef.current = () => quillRef.current?.getEditor() ?? null;
    }
  }, [charTargetRef, quillTargetRef, insertChar]);

  // Auto-focus + register when the editor is activated from its preview
  useEffect(() => {
    if (!editing) return;
    const quill = quillRef.current?.getEditor();
    quill?.focus();
    registerActive();
  }, [editing, registerActive]);

  // Small delay so clicking char buttons (inside container) doesn't close the editor
  const handleBlur = () => {
    setTimeout(() => {
      if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
        setEditing(false);
        if (quillTargetRef) quillTargetRef.current = null;
      }
    }, 150);
  };

  const isEmpty = !value || value === "<p><br></p>";

  return (
    <Box ref={containerRef} onBlur={handleBlur}>
      {editing ? (
        <>
          {/* Fallback inline panel only when no shared panel is wired in */}
          {!charTargetRef ? <SpecialCharPanel onInsert={insertChar} /> : null}

          {/* Quill editor — one instance mounted at a time */}
          <Box
            sx={{
              "& .ql-toolbar": {
                borderRadius: "4px 4px 0 0",
                padding: "3px 6px",
                flexWrap: "wrap",
                "& button": { padding: "2px 4px", width: "auto" },
              },
              "& .ql-container": { minHeight: 56, fontSize: "0.9rem", borderRadius: "0 0 4px 4px" },
              "& .ql-editor": { minHeight: 56, padding: "6px 10px" },
              "& .ql-editor img": { maxWidth: "100%", maxHeight: 200, borderRadius: 2, display: "block", margin: "4px 0" },
              "& .ql-editor.ql-blank::before": { fontSize: "0.875rem" },
            }}
          >
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={value || ""}
              onChange={handleChange}
              onFocus={registerActive}
              modules={modules}
              formats={showMathTools ? BLANK_ANSWER_MATH_FORMATS : BLANK_ANSWER_FORMATS}
              placeholder={placeholder}
            />
          </Box>
        </>
      ) : (
        /* Preview — click to activate editor */
        <Box
          onClick={() => setEditing(true)}
          sx={{
            minHeight: 40,
            border: "1px solid rgba(0,0,0,0.23)",
            borderRadius: 1,
            px: 1.5,
            py: 0.875,
            cursor: "text",
            fontSize: "0.9rem",
            lineHeight: 1.5,
            color: isEmpty ? "text.disabled" : "text.primary",
            "&:hover": { borderColor: "rgba(0,0,0,0.87)" },
            "& img": { maxWidth: "100%", maxHeight: 120, borderRadius: 1, verticalAlign: "middle" },
            "& p": { margin: 0 },
            "& strong": { fontWeight: 700 },
            "& em": { fontStyle: "italic" },
          }}
        >
          {isEmpty ? (
            placeholder || "Click to edit"
          ) : (
            <span dangerouslySetInnerHTML={{ __html: value }} />
          )}
        </Box>
      )}
    </Box>
  );
};

const FillInBlankEditor = ({
  blanks,
  setBlanks,
  isMulti = false,
  blankLabels,
  setBlankLabels,
  blankMarks,
  setBlankMarks,
  blankLabelPositions,
  setBlankLabelPositions,
  fillMarksMode = "sum",
  fillFixedTotal = 1,
}) => {
  const hasLabels = typeof setBlankLabels === "function";
  const hasMarks = typeof setBlankMarks === "function";
  const hasPositions = typeof setBlankLabelPositions === "function";
  const fixedMode = fillMarksMode === "fixed";
  const charTargetRef = useRef(null);
  const insertIntoActive = useCallback((char) => charTargetRef.current?.(char), []);

  const addBlank = () => {
    setBlanks([...blanks, [""]]);
    if (hasLabels) setBlankLabels([...(blankLabels || []), ""]);
    if (hasMarks) setBlankMarks([...(blankMarks || []), 1]);
    if (hasPositions) setBlankLabelPositions([...(blankLabelPositions || []), ""]);
  };

  const removeBlank = (blankIndex) => {
    if (blanks.length <= 1) return;
    setBlanks(blanks.filter((_, i) => i !== blankIndex));
    if (hasLabels) setBlankLabels((blankLabels || []).filter((_, i) => i !== blankIndex));
    if (hasMarks) setBlankMarks((blankMarks || []).filter((_, i) => i !== blankIndex));
    if (hasPositions) setBlankLabelPositions((blankLabelPositions || []).filter((_, i) => i !== blankIndex));
  };

  const updateBlankMarks = (blankIndex, value) => {
    const next = [...(blankMarks || [])];
    while (next.length < blanks.length) next.push(1);
    next[blankIndex] = Math.max(0, Number(value) || 0);
    setBlankMarks(next);
  };

  const addAcceptedAnswer = (blankIndex) => {
    const updated = [...blanks];
    updated[blankIndex] = [...updated[blankIndex], ""];
    setBlanks(updated);
  };

  const removeAcceptedAnswer = (blankIndex, ansIndex) => {
    if (blanks[blankIndex].length <= 1) return;
    const updated = [...blanks];
    updated[blankIndex] = updated[blankIndex].filter((_, i) => i !== ansIndex);
    setBlanks(updated);
  };

  const updateAnswer = (blankIndex, ansIndex, value) => {
    const updated = [...blanks];
    updated[blankIndex] = [...updated[blankIndex]];
    updated[blankIndex][ansIndex] = value;
    setBlanks(updated);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {isMulti ? "Blanks (multiple)" : "Blank"} — provide accepted answers for each
      </Typography>
      <SpecialCharPanel onInsert={insertIntoActive} />
      {blanks.map((answers, blankIndex) => (
        <Box key={blankIndex} sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Chip
              label={`Blank ${blankIndex + 1}`}
              size="small"
              sx={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", color: "#fff", fontWeight: 500 }}
            />
            {hasMarks && (
              fixedMode ? (
                <Chip
                  label={getFixedMarksPerBlankLabel(fillFixedTotal, blanks.length)}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700, ml: 1.5 }}
                />
              ) : (
                <TextField
                  type="number"
                  size="small"
                  label="Marks"
                  value={(blankMarks || [])[blankIndex] ?? 1}
                  onChange={(e) => updateBlankMarks(blankIndex, e.target.value)}
                  inputProps={{ min: 0, step: 0.25 }}
                  sx={{ width: 88, ml: 1.5 }}
                />
              )
            )}
            {blanks.length > 1 && (
              <IconButton
                size="small"
                color="error"
                onClick={() => removeBlank(blankIndex)}
                disabled={blanks.length <= 1}
                sx={{ ml: "auto" }}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Label — rich text, supports images (e.g. a shape picture instead of "Orange oval") */}
          {hasLabels && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.4, fontWeight: 500 }}>
                Label
              </Typography>
              <BlankAnswerEditor
                value={(blankLabels || [])[blankIndex] ?? ""}
                onChange={(val) => {
                  const next = [...(blankLabels || [])];
                  next[blankIndex] = val;
                  setBlankLabels(next);
                  if (hasPositions) {
                    const posNext = [...(blankLabelPositions || [])];
                    while (posNext.length < blanks.length) posNext.push("");
                    if (!posNext[blankIndex]) {
                      posNext[blankIndex] = inferBlankLabelPosition(val);
                      setBlankLabelPositions(posNext);
                    }
                  }
                }}
                placeholder="Label (e.g. onions, ½ + □ = 6)"
                charTargetRef={charTargetRef}
              />
              {hasPositions && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={(blankLabelPositions || [])[blankIndex] || inferBlankLabelPosition((blankLabels || [])[blankIndex])}
                  onChange={(_, val) => {
                    if (!val) return;
                    const next = [...(blankLabelPositions || [])];
                    while (next.length < blanks.length) next.push("");
                    next[blankIndex] = val;
                    setBlankLabelPositions(next);
                  }}
                  sx={{ mt: 0.75 }}
                >
                  <ToggleButton value="before" sx={{ px: 1.25, fontSize: "0.72rem", textTransform: "none" }}>
                    Before blank
                  </ToggleButton>
                  <ToggleButton value="after" sx={{ px: 1.25, fontSize: "0.72rem", textTransform: "none" }}>
                    After blank
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
          )}

          {answers.map((ans, ansIndex) => (
            <Box key={ansIndex} sx={{ mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.4, fontWeight: 500 }}>
                    {ansIndex === 0 ? "Primary answer" : `Accepted answer ${ansIndex + 1}`}
                  </Typography>
                  <BlankAnswerEditor
                    value={ans}
                    onChange={(val) => updateAnswer(blankIndex, ansIndex, val)}
                    placeholder={ansIndex === 0 ? "Primary answer" : `Alternative ${ansIndex + 1}`}
                    charTargetRef={charTargetRef}
                  />
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeAcceptedAnswer(blankIndex, ansIndex)}
                  disabled={answers.length <= 1}
                  sx={{ mt: 3.5 }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button size="small" startIcon={<Add />} onClick={() => addAcceptedAnswer(blankIndex)} sx={{ mt: 0.5 }}>
            Add Alternative
          </Button>
        </Box>
      ))}
      <Button size="small" startIcon={<Add />} onClick={addBlank}>
        Add Blank
      </Button>
    </Box>
  );
};

export default FillInBlankEditor;
